import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { getCustomers } from "../../../../services/customers-api";
import { createCart } from "../../../../services/pos-api";
import { handleFormattedValidationErrors } from "../../../../utils/errorHandlers";
import MinusIcon from "../../../../assets/minus-icon.svg";
import PlusIcon from "../../../../assets/plus-icon.svg";
import { useAppSelector } from "../../../../store/hooks";
import CartIcon from "../../../../assets/icons/CartIcon";
import DownArrow from "../../../../assets/icons/DownArrow";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import RedDeleteIcon from "../../../../assets/icons/RedDeleteIcon";
import DeleteConfirmationModal from "./DeleteConfirmationModal";


const CartSidebar = ({
  cartItems,
  onUpdateQuantity,
  onRemoveItem,
  onClearCart,
  onCancel,
  onNavigateToCategories,
  onSaleSuccess,
}) => {
  const [customerName, setCustomerName] = useState("");
  const [shippingFee, setShippingFee] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Delete modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState(""); // "item" or "cart"
  const [itemToDelete, setItemToDelete] = useState(null);

  // Customer dropdown states
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersError, setCustomersError] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [quantityInputs, setQuantityInputs] = useState({});
  const customerDropdownRef = useRef(null);
  const customerInputRef = useRef(null);
  const customerSearchInputRef = useRef(null);
  const hasLoadedCustomers = useRef(false);
  const activeInputRef = useRef(null);
  const previousSearchTerm = useRef("");
  const { isPartsURL, isAccessoriesURL } = useAppSelector((state) => state.domain);
  // Calculate dropdown position
  const calculateDropdownPosition = () => {
    if (customerInputRef.current) {
      const rect = customerInputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  };

  // Fetch customers from API
  const fetchCustomers = async (searchTerm = "") => {
    try {
      setCustomersLoading(true);
      setCustomersError(null);

      const { response, error } = await getCustomers(searchTerm, 1, 50); // Get first 50 customers

      if (error) {
        setCustomersError(error.message);
      } else {
        setCustomers(response.data || []);
      }
    } catch (error) {
      setCustomersError("Failed to load customers");
      console.error("Error fetching customers:", error);
    } finally {
      setCustomersLoading(false);
    }
  };


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        customerDropdownRef.current &&
        !customerDropdownRef.current.contains(event.target)
      ) {
        setIsCustomerDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close dropdown when clicking outside
  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setCustomerName(customer.name);
    setCustomerSearchTerm("");
    setIsCustomerDropdownOpen(false);
  };

  const getItemKey = (item) =>
    `${item.id}-${item.condition}-${item.unique_id || "no-unique-id"}`;

  useEffect(() => {
    setQuantityInputs((prev) => {
      const next = {};
      let hasChanges = false;

      cartItems.forEach((item) => {
        const key = getItemKey(item);
        const itemQuantity =
          item.quantity !== undefined && item.quantity !== null
            ? String(item.quantity)
            : "";
        const prevValue = prev[key];
        
        // Don't update if this input is currently focused (user is typing)
        if (activeInputRef.current === key) {
          next[key] = prevValue !== undefined ? prevValue : itemQuantity;
          return;
        }
        
        // Update to cart quantity if different from previous value
        if (prevValue !== itemQuantity) {
          next[key] = itemQuantity;
          hasChanges = true;
        } else {
          next[key] = itemQuantity;
        }
      });

      // Only update state if there are actual changes
      return hasChanges ? next : prev;
    });
  }, [cartItems]);

  // Load customers only when dropdown is opened for the first time
  const handleCustomerDropdownClick = () => {
    if (isCustomerDropdownOpen) {
      setIsCustomerDropdownOpen(false);
      setCustomerSearchTerm(""); // Clear search term when closing
    } else {
      setCustomerSearchTerm(""); // Clear search term when opening
      if (!hasLoadedCustomers.current) {
        fetchCustomers();
        hasLoadedCustomers.current = true;
      } else {
        // Reload all customers when reopening
        fetchCustomers("");
      }
      calculateDropdownPosition();
      setIsCustomerDropdownOpen(true);
      setTimeout(() => customerSearchInputRef.current?.focus(), 0);
    }
  };

  // Debounce search - only when dropdown is open and user is actively searching
  useEffect(() => {
    if (!isCustomerDropdownOpen || !hasLoadedCustomers.current) return;
    
    // If user cleared the search (was typing, now empty), reload all customers
    if (customerSearchTerm === "" && previousSearchTerm.current !== "") {
      previousSearchTerm.current = "";
      fetchCustomers("");
      return;
    }
    
    // Don't trigger if search term is empty on initial open (already loaded in handleCustomerDropdownClick)
    if (customerSearchTerm === "") return;
    
    const timeoutId = setTimeout(() => {
      if (customerSearchTerm.length >= 2) {
        // Search when user has typed at least 2 characters
        previousSearchTerm.current = customerSearchTerm;
        fetchCustomers(customerSearchTerm);
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [customerSearchTerm, isCustomerDropdownOpen]);

  const handleCustomerSearch = (e) => {
    const searchTerm = e.target.value;
    setCustomerSearchTerm(searchTerm);
    calculateDropdownPosition();
    setIsCustomerDropdownOpen(true);
  };

  const calculateSubtotal = (item) => {
    const price =
      typeof item.price === "number" ? item.price : parseFloat(item.price) || 0;
    return (price * item.quantity).toFixed(2);
  };

  const calculateTotal = () => {
    const subtotal = cartItems.reduce((sum, item) => {
      const price =
        typeof item.price === "number"
          ? item.price
          : parseFloat(item.price) || 0;
      return sum + price * item.quantity;
    }, 0);
    const shipping = parseFloat(shippingFee) || 0;
    return (subtotal + shipping).toFixed(2);
  };

  const formatPrice = (price) => {
    const numPrice = typeof price === "number" ? price : parseFloat(price) || 0;
    return `€${numPrice.toFixed(2)}`;
  };

  const handleQuantityInputChange = (item, value) => {
    const itemKey = getItemKey(item);

    if (!/^\d*$/.test(value)) {
      return;
    }

    // Update local state immediately for responsive typing
    setQuantityInputs((prev) => ({ ...prev, [itemKey]: value }));

    if (value === "") {
      return;
    }

    const parsed = Number.parseInt(value, 10);
    if (Number.isNaN(parsed)) {
      return;
    }

    const hasStockLimit = typeof item.stock === "number" && item.stock >= 0;
    const maxAllowed = hasStockLimit ? item.stock : parsed;

    // Cap the value at maximum available stock
    const safeValue = Math.max(0, Math.min(parsed, maxAllowed));

    if (hasStockLimit && parsed > maxAllowed) {
      // Show warning and cap at max stock
      toast.warning(`Only ${maxAllowed} in stock`);
      // Update input to show the capped value
      setQuantityInputs((prev) => ({ ...prev, [itemKey]: String(maxAllowed) }));
    }

    // Update cart with the safe value if different from current quantity
    if (safeValue !== item.quantity) {
      onUpdateQuantity(
        item.id,
        item.condition,
        safeValue,
        item.unique_id || null
      );
    }
  };

  const handleQuantityInputFocus = (item) => {
    const itemKey = getItemKey(item);
    activeInputRef.current = itemKey;
  };

  const handleQuantityInputBlur = (item) => {
    const itemKey = getItemKey(item);
    activeInputRef.current = null;

    setQuantityInputs((prev) => {
      const current = prev[itemKey];
      
      // If empty, set to current cart quantity
      if (current === "" || current === undefined) {
        const fallback =
          item.quantity !== undefined && item.quantity !== null
            ? String(item.quantity)
            : "1";
        return { ...prev, [itemKey]: fallback };
      }

      return prev;
    });
  };

  const handleQuantityInputWheel = (e) => {
    // Prevent scroll from changing the number input value
    e.target.blur();
    e.preventDefault();
  };

  // Delete modal handlers
  const handleDeleteItem = (item) => {
    setItemToDelete(item);
    setDeleteType("item");
    setShowDeleteModal(true);
  };

  const handleClearCart = () => {
    setDeleteType("cart");
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (deleteType === "item" && itemToDelete) {
      onRemoveItem(itemToDelete.id, itemToDelete.condition, itemToDelete.unique_id || null);
      toast.success(`${itemToDelete.name} removed from cart`);
    } else if (deleteType === "cart") {
      onClearCart();
      toast.success("Cart cleared successfully");
    }
    setShowDeleteModal(false);
    setItemToDelete(null);
    setDeleteType("");
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
    setDeleteType("");
  };

  // Save Sale functionality
  const parsePrice = (price) => {
    return typeof price === "number" ? price : parseFloat(price) || 0;
  };

  const formatPriceForApi = (price) => {
    const parsed = parsePrice(price);
    return parseFloat(parsed.toFixed(2));
  };

  const handleSaveSale = async () => {
    if (cartItems.length === 0) {
      toast.error("Cart is empty. Add items to save sale.");
      return;
    }

    if (!selectedCustomer) {
      toast.error("Please select a customer before saving the sale.");
      return;
    }

    const itemsPayload = cartItems
      .filter((item) => item.quantity > 0)
      .map((item) => {
        const payload = {
          product: item.id,
          qty: item.quantity,
          sell_price: formatPriceForApi(item.price),
        };

        // Do not send unique_id when saving sale
        // unique_id is only used for tracking in cart, not for API

        return payload;
      });

    if (itemsPayload.length === 0) {
      toast.error("Unable to save sale with zero quantity items.");
      return;
    }

    const shippingAmountValue = shippingFee !== "" ? shippingFee : "0";

    const cartPayload = {
      customer: selectedCustomer.id,
      shipping_amount: shippingAmountValue.toString(),
      send_by_post: false,
      previous_balance: "0",
      paid_amount: "0",
      flat_discount: "0",
      status: "DRAFT",
      items: itemsPayload,
    };

    try {
      setIsSaving(true);

      const response = await createCart(cartPayload);

      // Success - show toast and navigate
      toast.success(response?.message || "Sale saved successfully!");

      // Trigger product refresh
      if (onSaleSuccess) {
        onSaleSuccess();
      }

      // Clear cart and navigate to categories
      onClearCart();
      setSelectedCustomer(null);
      setCustomerName("");
      setCustomerSearchTerm("");
      setShippingFee("");
      hasLoadedCustomers.current = false;
      if (onNavigateToCategories) {
        onNavigateToCategories();
      }
    } catch (error) {
      console.error("Error saving sale:", error);
      handleFormattedValidationErrors(
        error,
        "Failed to save sale. Please try again."
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Handle Cancel - hide the cart without clearing items
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div className="cart-sidebar">
      <div className="cart-header">
        <div className="cart-title">
          <CartIcon color={isPartsURL ? "var(--primary-color-parts)" : isAccessoriesURL ? "var(--primary-color-acc)" : "var(--primary-color-mobile)"} />
          <span>
            Cart
            <span className="cart-title-items">
              (Total{" "}
              <span
                className="cart-title-items-quantity"
              >
                {cartItems.reduce((sum, item) => sum + item.quantity, 0)}
              </span>{" "}
              Quantity)
            </span>
          </span>
        </div>
        <button className={isPartsURL ? "clear-cart-btn-parts" : isAccessoriesURL ? "clear-cart-btn-acc" : "clear-cart-btn"} onClick={handleClearCart}>
          Clear Cart
        </button>
      </div>

      <div className="cart-content">
        <div className="cart-scrollable-content">
          {/* Customer Details */}
          <div className="customer-section">
            <h3 className="section-title">Customer Details</h3>
            <div
              className="customer-dropdown-container-pos"
              ref={customerDropdownRef}
            >
              <div className="customer-input-wrapper">
                <div
                  ref={customerInputRef}
                  className={isPartsURL ? "customer-dropdown-btn-parts" : isAccessoriesURL ? "customer-dropdown-btn-acc" : "customer-dropdown-btn"}
                  onClick={handleCustomerDropdownClick}
                >
                  {customerName || "Choose or search customer"}
                </div>
                <div className="customer-dropdown-arrow">
                  <DownArrow />
                </div>
              </div>

              {isCustomerDropdownOpen && (
                <div
                  className="customer-dropdown"
                  style={{
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                    width: `${dropdownPosition.width}px`,
                  }}
                >
                  <div className="customer-search-container">
                    <div className="customer-search-icon">
                      <SearchIcon />
                    </div>
                    <input
                      ref={customerSearchInputRef}
                      type="text"
                      className={isPartsURL ? "customer-search-input-parts" : isAccessoriesURL ? "customer-search-input-acc" : "customer-search-input"}
                      placeholder="Search customer name..."
                      value={customerSearchTerm}
                      onChange={handleCustomerSearch}
                    />
                  </div>

                  <div className="customer-list">
                    {customersLoading ? (
                      <div className="customer-option loading">
                        Loading customers...
                      </div>
                    ) : customersError ? (
                      <div className="customer-option error">
                        {customersError}
                        <button
                          onClick={() => fetchCustomers(customerSearchTerm)}
                          className="retry-btn-small"
                        >
                          Retry
                        </button>
                      </div>
                    ) : customers.length > 0 ? (
                      customers.map((customer) => (
                        <div
                          key={customer.id}
                          className="customer-option"
                          onClick={() => handleCustomerSelect(customer)}
                        >
                          {customer.name}
                        </div>
                      ))
                    ) : (
                      <div className="customer-option no-results">
                        No customers found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Order Details */}
          <div className="order-section">
            <h3 className="section-title" style={{ marginBottom: "12px" }}>
              Order Details
            </h3>
            <div className="order-items">
              {cartItems.length === 0 ? (
                <div className="empty-cart">
                  <p>No items in cart</p>
                </div>
              ) : (
                [...cartItems].reverse().map((item) => (
                  <div
                    key={`${item.id}-${item.condition}-${item.unique_id || 'no-unique-id'}`}
                    className="order-item"
                  >
                    <div className="item-image">
                      {item.image ? (
                        <img src={item.image} alt={item.name} />
                      ) : (
                        <div className="item-image-placeholder">
                          <span className="placeholder-text">{item.name}</span>
                        </div>
                      )}
                      <span
                        className={`${isPartsURL ? "condition-badge-parts" : isAccessoriesURL ? "condition-badge-acc" : "condition-badge"} ${item.condition.toLowerCase()}`}
                      >
                        {item.condition}
                      </span>
                    </div>

                    <div className="item-details">
                      <div className="item-name">
                        <div>{item.name}</div>
                        {item.details && (
                          <div
                            style={{
                              fontSize: "0.8em",
                              color: "#666",
                              marginTop: "4px",
                            }}
                          >
                            {item.details}
                          </div>
                        )}
                      </div>
                      <div className="item-price">
                        {formatPrice(item.price)}
                      </div>
                      <div className="item-bottom-controls">
                        <div className="item-quantity-controls">
                          <button
                            className={`${isPartsURL ? "quantity-btn-parts" : isAccessoriesURL ? "quantity-btn-acc" : "quantity-btn"} minus`}
                            onClick={() =>
                              onUpdateQuantity(
                                item.id,
                                item.condition,
                                item.quantity - 1,
                                item.unique_id || null
                              )
                            }
                            disabled={item.quantity <= 0}
                            aria-label="Decrease quantity"
                          >
                            <img src={MinusIcon} alt="Minus" />
                          </button>
                          <input
                            type="number"
                            inputMode="numeric"
                            min="0"
                            max={item.stock || undefined}
                            className="quantity-input"
                            value={
                              quantityInputs[getItemKey(item)] !== undefined
                                ? quantityInputs[getItemKey(item)]
                                : item.quantity
                            }
                            onChange={(e) =>
                              handleQuantityInputChange(item, e.target.value)
                            }
                            onFocus={() => handleQuantityInputFocus(item)}
                            onBlur={() => handleQuantityInputBlur(item)}
                            onWheel={handleQuantityInputWheel}
                            aria-label="Quantity"
                          />
                          <button
                            className={`${isPartsURL ? "quantity-btn-parts" : isAccessoriesURL ? "quantity-btn-acc" : "quantity-btn"} plus`}
                            onClick={() =>
                              onUpdateQuantity(
                                item.id,
                                item.condition,
                                item.quantity + 1,
                                item.unique_id || null
                              )
                            }
                            disabled={item.quantity >= (item.stock || 0)}
                            aria-label="Increase quantity"
                          >
                            <img src={PlusIcon} alt="Plus" />
                          </button>
                        </div>

                        <div className="item-subtotal">
                          <span className="item-subtotal-text">
                            Sub Total:
                            <span className="item-subtotal-value">
                              {formatPrice(calculateSubtotal(item))}
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>

                    <button
                      className="remove-item-btn"
                      onClick={() => handleDeleteItem(item)}
                    >
                      <RedDeleteIcon />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Add empty space for 2 more products when only 1 product */}
          {cartItems.length === 1 && (
            <div className="empty-product-spaces">
              <div className="empty-product-space"></div>
              <div className="empty-product-space"></div>
            </div>
          )}
        </div>

        {/* Sticky Footer Section */}
        <div className="cart-sticky-footer">
          {/* Separator */}
          <div className="cart-separator"></div>

          {/* Shipping Details */}
          <div className="shipping-section">
            <h3 className="section-title">Shipping Fee</h3>
            <input
              type="number"
              className={isPartsURL ? "shipping-fee-input-parts" : isAccessoriesURL ? "shipping-fee-input-acc" : "shipping-fee-input"}
              placeholder="Enter fee"
              value={shippingFee}
              min="0"
              step="0.01"
              onChange={(e) => {
                const value = e.target.value;
                // Only allow positive numbers
                if (value === "" || (!isNaN(value) && parseFloat(value) >= 0)) {
                  setShippingFee(value);
                }
              }}
              onWheel={handleQuantityInputWheel}
            />
          </div>

          {/* Total and Actions */}
          <div className="cart-footer">
            <div className="total-section">
              <div className="total-amount">Total: €{calculateTotal()}</div>
            </div>
            <div className="action-buttons">
              <button
                className={isPartsURL ? "save-sale-btn-parts" : isAccessoriesURL ? "save-sale-btn-acc" : "save-sale-btn"}
                onClick={handleSaveSale}
                disabled={isSaving || cartItems.length === 0}
              >
                {isSaving ? "Saving..." : "Save Sale"}
              </button>
              <button
                className="cancel-btn"
                onClick={handleCancel}
                disabled={isSaving}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        deleteType={deleteType}
        itemName={itemToDelete?.name}
      />
    </div>
  );
};

export default CartSidebar;
